import { G } from '@mobily/ts-belt';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box } from '@mui/material';
import { BooleanField, TextField, useRecordContext } from 'react-admin';

import {
  B2bCompanyDocumentCategory,
  B2bCompanyDocumentDetails,
  categoryHasTypes,
  getB2bCompanyDocumentsSortColumns,
  getTypesForCategory,
} from '@Common/types/bo/b2b/companies';

import {
  B2bCompanyDocumentsCreate,
  DatagridTyped,
  EmptyScreen,
  List,
  Pagination,
} from '@sh4res/bo-common/components';
import { companyDocumentsTypeChoices } from '@sh4res/bo-common/components/B2bCompanyDocuments/choices';
import { IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { ResourceBasePath, useCanAccess, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { ColumnList, Resources } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bCompanyDocumentsByCategoryList';

const getDocumentsColumns = (resourceBasePath: ResourceBasePath) =>
  ({
    id: (
      <IdField
        reference={`${resourceBasePath}companies/documents`}
        label="resources.bo_common.b2b.documents.fields.id"
        data-testid={`${testId}_IdField`}
      />
    ),
    company_id: (
      <IdField
        reference={`${resourceBasePath}companies`}
        label="resources.bo_common.b2b.documents.fields.company_id"
        data-testid={`${testId}_CompanyIdField`}
      />
    ),
    b2b_campaign_id: (
      <IdField
        reference={`${resourceBasePath}campaigns`}
        label="resources.bo_common.b2b.documents.fields.b2b_campaign_id"
        data-testid={`${testId}_B2bCampaignIdField`}
      />
    ),
    savings_funds_request_id: (
      <IdField
        reference={`${resourceBasePath}savings_funds_requests`}
        label="resources.bo_common.b2b.documents.fields.savings_funds_request_id"
        data-testid={`${testId}_SavingsFundsRequestIdField`}
      />
    ),
    url: (
      <DocumentListViewerLink<B2bCompanyDocumentDetails>
        urlSource="document_url"
        nameSource="document_name"
        label="resources.bo_common.b2b.documents.fields.document_name"
        testId={`${testId}_Url`}
      />
    ),
    co_sepa_mandate_id: (
      <TextField
        label="resources.bo_common.b2b.documents.fields.co_sepa_mandate_id"
        data-testid={`${testId}_CoSepaMandateIdField`}
      />
    ),
    document_url: (
      <TextField
        label="resources.bo_common.b2b.documents.fields.document_url"
        data-testid={`${testId}_DocumentUrlField`}
      />
    ),
    document_name: (
      <TextField
        label="resources.bo_common.b2b.documents.fields.document_name"
        data-testid={`${testId}_DocumentNameField`}
      />
    ),
    document_comment: (
      <TextField
        label="resources.bo_common.b2b.documents.fields.document_comment"
        data-testid={`${testId}_DocumentCommentField`}
      />
    ),
    is_archived: (
      <BooleanField
        label="resources.bo_common.b2b.documents.fields.is_archived"
        data-testid={`${testId}_IsArchivedField`}
      />
    ),
    task_id: (
      <IdField
        // eslint-disable-next-line regex/invalid -- only in BO
        reference="tasks"
        label="resources.bo_common.b2b.documents.fields.task_id"
        data-testid={`${testId}_TaskIdField`}
      />
    ),
    type: (
      <SelectChipField
        choices={companyDocumentsTypeChoices}
        label="resources.bo_common.b2b.documents.fields.type"
        data-testid={`${testId}_TypeField`}
      />
    ),
    is_generated: (
      <BooleanField
        label="resources.bo_common.b2b.documents.fields.is_generated"
        data-testid={`${testId}_IsGeneratedField`}
      />
    ),
    source: (
      <TextField
        label="resources.bo_common.b2b.documents.fields.source"
        data-testid={`${testId}_SourceField`}
      />
    ),
    created_at: (
      <UTCDateField
        showTime={!isBoExternal()}
        label="resources.bo_common.b2b.documents.fields.created_at"
        data-testid={`${testId}_CreatedAtField`}
      />
    ),
    updated_at: (
      <UTCDateField
        showTime={!isBoExternal()}
        label="resources.bo_common.b2b.documents.fields.updated_at"
        data-testid={`${testId}_UpdatedAtField`}
      />
    ),
  }) satisfies ColumnList<B2bCompanyDocumentDetails & { url: string }>;

const getDisplayedColumns = (): (keyof ReturnType<typeof getDocumentsColumns>)[] =>
  isBoExternal()
    ? ['url', 'created_at']
    : ['id', 'type', 'url', 'source', 'created_at', 'is_archived', 'document_comment'];

interface B2bCompanyDocumentsByCategoryListProps {
  category: B2bCompanyDocumentCategory;
  companyId?: number;
}

export const B2bCompanyDocumentsByCategoryList = ({
  category,
  companyId: companyIdProp,
}: B2bCompanyDocumentsByCategoryListProps) => {
  const record = useRecordContext<{ id: number }>();
  const companyId = companyIdProp ?? record?.id;

  const resourceBasePath = useResourceBasePath();
  const resource = `${resourceBasePath}companies/documents` as Resources;

  const canUploadDocuments = useCanAccess({
    resource,
    action: 'create',
  });

  const typesForCategory = getTypesForCategory(category);
  const hasTypes = categoryHasTypes(category);
  const showUploadButton = hasTypes && canUploadDocuments && companyId !== undefined;

  // If no types for this category, show empty state
  if (!hasTypes) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          width: '100%',
        }}>
        <EmptyScreen
          label="resources.bo_common.b2b.documents.empty_category"
          svg={<EmptyFolderSvg />}
        />
      </Box>
    );
  }

  // Filter by company_id and document types for this category
  const filter = {
    ...(companyId !== undefined ? { company_id: companyId } : {}),
    is_archived: false,
    type: typesForCategory,
  };

  const emptyContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        width: '100%',
      }}>
      <EmptyScreen
        label="resources.bo_common.b2b.documents.empty_category"
        svg={<EmptyFolderSvg />}
      />
    </Box>
  );

  return (
    <Box>
      <List
        resource={resource}
        filter={filter}
        sort={{ field: 'created_at', order: 'DESC' }}
        pagination={<Pagination />}
        disableSyncWithLocation={true}
        storeKey={`documents-${category}`}
        title=" "
        actions={false}
        empty={emptyContent}>
        <DatagridTyped<B2bCompanyDocumentDetails & { url: string }>
          sortableColumns={isBoExternal() ? [] : getB2bCompanyDocumentsSortColumns}
          columnRegistry={getDocumentsColumns(resourceBasePath)}
          displayedColumns={getDisplayedColumns()}
          bulkActionButtons={false}
          rowClick="show"
          showCard={false}
        />
      </List>
      {showUploadButton && G.isNotNullable(companyId) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <B2bCompanyDocumentsCreate
            resource={resource}
            icon={<FileUploadIcon />}
            label="resources.bo_common.b2b.documents.button"
            record={{ company_id: companyId }}
            documentTypes={typesForCategory}
          />
        </Box>
      )}
    </Box>
  );
};
